import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api, { apiError } from "../lib/api";
import { useSeo, breadcrumb } from "../lib/seo";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/c204c004-4e3b-4de6-bafd-66db5d32f428/images/a15a97027f5d2808fd5dc7ea989464d183561c6a3b7238b4a54e22944cebbf4d.jpeg";

const TILES = [
  { icon: Phone, title: "Call us", lines: ["Reservations: 800 EUROPCAR (800 38767227)", "24/7 roadside assistance included"] },
  { icon: MessageCircle, title: "WhatsApp", lines: ["Message us any time", "Replies within business hours"] },
  { icon: Mail, title: "Email", lines: ["reservations@europcar.com", "customer.service@europcar.ae"] },
];

const SUBJECTS = ["Booking enquiry", "Existing rental", "Billing or invoice", "Feedback", "Something else"];

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useSeo({
    title: "Contact Europcar Dubai & Northern Emirates | We're Here to Help",
    description: "Reach Europcar UAE by phone (800 EUROPCAR), WhatsApp, email or the contact form. 24/7 roadside assistance and 14 branches across the UAE.",
    jsonLd: [breadcrumb([["Home", "/"], ["Contact", "/contact"]])],
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/contact", form);
      setDone(r.data.contact_id);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 promo-stripe">
          <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="contact-heading">{t("We're here to help.")}</h1>
          <p className="text-white/80 text-sm mt-2 max-w-2xl">{t("Questions about a booking, a charge or anything on four wheels — reach us however suits you best.")}</p>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full flex-1">
        <div className="grid sm:grid-cols-3 gap-4">
          {TILES.map((tile) => (
            <div key={tile.title} className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`contact-tile-${tile.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <tile.icon className="w-6 h-6 text-[#009900] mb-2" />
              <h3 className="font-bold text-sm text-[#0F1E17]">{t(tile.title)}</h3>
              {tile.lines.map((l) => <p key={l} className="text-xs text-[#57685e] mt-1">{t(l)}</p>)}
            </div>
          ))}
        </div>
        <div className="mt-4 bg-white border border-[#E2E8F0] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid="contact-branch-locator">
          <p className="text-sm text-[#41504a] flex items-center gap-2"><MapPin className="w-5 h-5 text-[#009900]" /> {t("Prefer face to face? Find your nearest of our 14 branches.")}</p>
          <Link to="/locations" className="btn-cta rounded px-5 py-2.5 text-xs">{t("Branch locator")}</Link>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mt-10 items-start">
          <div>
            <div className="promo-stripe mb-4">
              <h2 className="heading-upper text-xl text-[#144733]">{t("Send us a message")}</h2>
            </div>
            {done ? (
              <div className="bg-[#f0f9f0] border border-[#009900]/25 rounded-2xl p-8 text-center" data-testid="contact-success">
                <CheckCircle2 className="w-10 h-10 text-[#009900] mx-auto mb-3" />
                <h3 className="font-bold text-[#0F1E17]">{t("Message received — ref {{id}}", { id: done })}</h3>
                <p className="text-sm text-[#57685e] mt-2">{t("Our customer care team replies within one business day. A confirmation email is on its way (demo mode — logged, not sent).")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="field-label">{t("Full name *")}</label><input className="field-input" required value={form.name} onChange={set("name")} data-testid="contact-name-input" /></div>
                  <div><label className="field-label">{t("Email *")}</label><input type="email" className="field-input" required value={form.email} onChange={set("email")} data-testid="contact-email-input" /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="field-label">{t("Phone")}</label><input className="field-input" placeholder="+971" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" /></div>
                  <div><label className="field-label">{t("Topic")}</label>
                    <select className="field-input" value={form.subject} onChange={set("subject")} data-testid="contact-subject-select">
                      {SUBJECTS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                    </select>
                  </div>
                </div>
                <div><label className="field-label">{t("Message *")}</label><textarea className="field-input min-h-[120px]" required value={form.message} onChange={set("message")} data-testid="contact-message-input" /></div>
                <button type="submit" disabled={busy} className="btn-cta rounded px-6 py-3 text-xs flex items-center gap-2 disabled:opacity-50" data-testid="contact-submit-btn">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("Send message")}
                </button>
              </form>
            )}
          </div>
          <img src={HERO} alt="Europcar customer care agent ready to help" className="rounded-2xl w-full object-cover border border-[#E2E8F0]" loading="lazy" />
        </div>
      </main>
      <Footer />
    </div>
  );
}
